import { defineStore } from 'pinia';
import { useCustomerStore } from '@/stores/customer';

export const useAgentStore = defineStore('agent', {
	state: () => ({
		// Customers
		customers: [],
		customer_id: null,
		customer: null,

		// Affiliates & Children
		children: [],
		children_affiliates: [],

		// Transactions
		transactions: [],

		// Revenue Report
		revenue_report: {},
		revenue_report_per_customer_table: null,
		revenue_report_per_product_table: null,
		revenue_report_totals: {},
		// Gambling Report
		gambling_report: {},
		gambling_report_record: {},
		selected_customer_id: null,
		selected_report: null,

		// General
		page: 1,
		is_loading: false,
		cancel_token_source: null,
		per_page_options: [10, 25, 50, 100],
		per_page: 100,

		// descendants
		descendants: [],

		// Children agents
		children_agents_abort_controller: null,

		transaction_totals: [],
		sportsbook_group_limits: [],
		sportsbook_group_limits_info: [],

		// Agent privileges. The /me resource used by this app does not carry the
		// agent flags (CustomerResourceLegacy does, the newer CustomerResource
		// does not), so they come from /account/affiliate/info instead.
		agent_info: null,
		highlighted_customer_id: null,
		auto_expanded_ids: [],
		auto_expanded_pages: {},
		full_tree: null,
		is_tree_expanded_all: false,
		all_expandable_ids: [],
		expanded_customer_ids: [],
	}),
	getters: {
		isAllExpanded(state) {
			if (!state.is_tree_expanded_all || !state.full_tree) return false;
			if (!state.all_expandable_ids || state.all_expandable_ids.length === 0) return false;
			return state.all_expandable_ids.every((id) => state.expanded_customer_ids.includes(Number(id)));
		},
		canCreateAffiliate(state) {
			return state.agent_info?.can_create_affiliate === true;
		},
		isRootAgent(state) {
			const customerStore = useCustomerStore();
			const me = customerStore.getCustomer?.id;
			if (me == null || !Array.isArray(state.children_affiliates)) return false;

			const self = state.children_affiliates.find((c) => Number(c.id) === Number(me));
			if (!self) return false;

			return self.parent_id === null;
		},
		showRegisterModal() {
			if (!this.agentEnabled) return true;
			if (this.agentEnabled && !this.isHybridMode) return false;
			if (this.agentEnabled && this.isHybridMode) return true;
		},
		isAgentOrAffiliate() {
			const customerStore = useCustomerStore();
			return customerStore.customer?.is_affiliate || customerStore.customer?.parent_id != null;
		},
		isHybridMode() {
			return process.env.VUE_APP_AGENT_TYPE == 'hybrid' ? true : false;
		},
		agentEnabled() {
			return process.env.VUE_APP_AGENT_ENABLED == 'true' ? true : false;
		},
		agentCommissions() {
			if (process.env.VUE_APP_AGENT_COMMISSIONS == 'false') return false;
			return true;
		},
		isCustomerAgent() {
			const customerStore = useCustomerStore();

			// Check if customer is logged in
			let is_logged_in = customerStore.isLoggedIn;
			if (!is_logged_in) return false

			return customerStore.customer.is_affiliate;
		},
		transactionTotals(state) {
			return state.transaction_totals
		},
		getSportsbookGroupLimits(state) {
			return state.sportsbook_group_limits;
		}
	},
	actions: {
		getDescendants() {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/descendants', { headers: headers }) .then((response) => {
				this.descendants = response.data.data;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		getChildrenAffiliates() {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/children', { headers: headers }) .then((response) => {
				this.children_affiliates = response.data.children;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		getChildrenAgentsByParents(payload) {
			if (this.children_agents_abort_controller) {
				this.children_agents_abort_controller.abort();
			}

			const controller = new AbortController();
			this.children_agents_abort_controller = controller;

			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/children/agents', {
				headers: headers,
				params: payload,
				signal: controller.signal,
			}).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				if (error?.code === 'ERR_CANCELED') {
					return Promise.reject(error);
				}

				console.log(error.response);

				return Promise.reject(error.response);
			}).finally(() => {
				if (this.children_agents_abort_controller === controller) {
					this.children_agents_abort_controller = null;
				}
			});
		},
		// Search & Hierarchy
		searchCustomers(query, customer_id = null) {
			let headers = {
				'Accept': 'application/json'
			};
			let params = { query: query };
			if (customer_id) params.customer_id = customer_id;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/search', {
				headers: headers,
				params: params
			}).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				return Promise.reject(error.response);
			});
		},
		fetchCustomerBranch(customer_id) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/branch/' + customer_id, {
				headers: headers
			}).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				return Promise.reject(error.response);
			});
		},
		fetchCustomerTree(customer_id = null) {
			let headers = {
				'Accept': 'application/json'
			};
			let params = {};
			if (customer_id) params.customer_id = customer_id;

			this.is_loading = true;
			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/tree', {
				headers: headers,
				params: params
			}).then((response) => {
				const collectExpandableIds = (nodes) => {
					let ids = [];
					if (!Array.isArray(nodes)) return ids;
					for (const node of nodes) {
						if (Number(node.children_count) > 0 || (Array.isArray(node.children) && node.children.length > 0)) {
							ids.push(Number(node.id));
						}
						if (Array.isArray(node.children) && node.children.length > 0) {
							ids = ids.concat(collectExpandableIds(node.children));
						}
					}
					return ids;
				};

				const expandableIds = collectExpandableIds(response.data.tree);
				this.full_tree = response.data.tree;
				this.all_expandable_ids = expandableIds;
				this.expanded_customer_ids = [...expandableIds];
				this.is_tree_expanded_all = true;
				this.is_loading = false;
				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;
				return Promise.reject(error.response);
			});
		},
		setCustomerExpanded(id, isExpanded) {
			const numericId = Number(id);
			const index = this.expanded_customer_ids.indexOf(numericId);
			if (isExpanded) {
				if (index === -1) {
					this.expanded_customer_ids.push(numericId);
				}
			} else {
				if (index !== -1) {
					this.expanded_customer_ids.splice(index, 1);
				}
			}
		},
		removeCustomerAndDescendants(customer) {
			if (!customer) return;
			const idsToRemove = [Number(customer.id)];
			const collectDescendantIds = (node) => {
				if (Array.isArray(node?.children)) {
					for (const child of node.children) {
						idsToRemove.push(Number(child.id));
						collectDescendantIds(child);
					}
				}
			};
			collectDescendantIds(customer);
			this.expanded_customer_ids = this.expanded_customer_ids.filter(id => !idsToRemove.includes(id));
		},
		expandAll() {
			this.expanded_customer_ids = [...this.all_expandable_ids];
			this.is_tree_expanded_all = true;
		},
		clearExpandedCustomers() {
			this.expanded_customer_ids = [];
			this.all_expandable_ids = [];
		},
		// Customers
		fetchCustomers(payload = null) {
			let headers = {
				'Accept': 'application/json'
			};

			this.page = payload?.page || 1;
			this.is_loading = true;

			if (this.is_tree_expanded_all) {
				this.fetchCustomerTree(payload?.customer_id || null);
			}

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/list', { headers: headers, params: payload }) .then((response) => {
				this.customers = response.data.customers;
				this.customer = response.data.customer;
				this.is_loading = false;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;

				return Promise.reject(error.response);
			});
		},
		deleteCustomer(customer_id) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.delete(process.env.VUE_APP_API_URL + '/account/affiliate/users/' + customer_id, { headers: headers }) .then((response) => {

				return Promise.resolve(response);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		saveCustomer(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/users', payload, { headers: headers }) .then((response) => {

				return Promise.resolve(response);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		setCustomerId(customer_id) {
			this.customer_id = customer_id;

			if (customer_id == null) this.customer = null;

			return Promise.resolve(customer_id);
		},
		fetchCustomer(customer_id) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/' + customer_id + '/edit', { headers: headers }) .then((response) => {
				this.customer = response.data;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		updateCustomer(customer_id, payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.patch(process.env.VUE_APP_API_URL + '/account/affiliate/users/' + customer_id, payload, { headers: headers }) .then((response) => {
				this.customer = response.data;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		fetchChildrens(payload) {
			let headers = {
				'Accept': 'application/json'
			};
			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/users/list', {
					headers: headers,
					params: payload
				})
				.then((response) => {
					return Promise.resolve(response.data);
				})
				.catch((error) => {
					console.log(error.response);
					return Promise.reject(error.response);
				});
		},
		// Transactions
		fetchTransactions(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			this.page = payload.page;
			this.is_loading = true;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/transaction', { headers: headers, params: payload }) .then((response) => {
				this.transactions = response.data;
				this.is_loading = false;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;

				return Promise.reject(error.response);
			});
		},
		fetchTransactionTotals(payload) {

			this.is_loading = true;

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/transaction/totals',  payload) .then((response) => {
				this.transaction_totals = response.data;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		fetchChildren() {
			let headers = {
				'Accept': 'application/json'
			};

			this.children = [];
			this.is_loading = true;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/transaction/create', { headers: headers }) .then((response) => {
				this.children = response.data.children;
				this.is_loading = false;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;

				return Promise.reject(error.response);
			});
		},
		saveTransaction(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/transaction', payload, { headers: headers }) .then((response) => {

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		// Revenue Report
		getChildrenRevenueReport(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			this.children = [];
			this.revenue_report = {};
			this.revenue_report_per_customer_table = null;
			this.revenue_report_per_product_table = null;
			this.is_loading = true;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue', { headers: headers, params: { from: payload.from, to: payload.to } }) .then((response) => {
				this.children = response.data.children;
				this.is_loading = false;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;

				return Promise.reject(error.response);
			});
		},
		getRevenueReport(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			this.revenue_report = {};
			this.revenue_report_per_customer_table = null;
			this.revenue_report_per_product_table = null;

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue', payload, { headers: headers }) .then((response) => {
				this.revenue_report = response.data;

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},

		getRevenueReportTotals(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue-total', payload, { headers: headers }) .then((response) => {
				this.revenue_report_totals = response.data;

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		getRevenueReportForAgent(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue', payload, { headers: headers }).then((response) => {

					return Promise.resolve(response);
				})
				.catch((error) => {
					console.log(error.response);
					return Promise.reject(error.response);
				});
		},

		getRevenuePerCustomerTable(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue/per_customer_table', payload, { headers: headers }) .then((response) => {
				this.revenue_report_per_customer_table = response.data;

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		getRevenuePerProductTable(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue/per_product_table', payload, { headers: headers }) .then((response) => {
				this.revenue_report_per_product_table = response.data;

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		getRevenuePerCustomerPerProvider(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue/per-customer-per-provider', payload, { headers: headers }) .then((response) => {
				return Promise.resolve(response.data);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},

		getRevenuePerCustomerPerProviderLiveCasino(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/revenue/live-casino-per-customer-per-provider', payload, { headers: headers }) .then((response) => {
				return Promise.resolve(response.data);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},

		// Gambling Report
		getChildrenGamblingReport(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			this.children = [];
			this.selected_customer_id = null;
			this.selected_report = null;
			this.gambling_report = {};
			this.is_loading = true;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/report/gambling', { headers: headers, params: { from: payload.from, to: payload.to } }) .then((response) => {
				this.children = response.data.children;
				this.is_loading = false;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);
				this.is_loading = false;

				return Promise.reject(error.response);
			});
		},
		getGamblingReport(payload) {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/v2/gambling', payload, { headers: headers }) .then((response) => {
				this.gambling_report = response.data.results;

				return Promise.resolve(response);
			})
			.catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		showSlotsGamblingReport(payload) {
			// Check and cancel the previous request
			if (this.cancel_token_source) {
				this.cancel_token_source.cancel('Previous request canceled');
			}

			// Create a new cancel token
			this.cancel_token_source = global.axios.CancelToken.source();

			let headers = {
				'Accept': 'application/json'
			};

			this.is_loading = true;
			// this.gambling_report_record = {};
			this.selected_customer_id = payload.customer_id;
			this.selected_report = 'slots';

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/slots', payload, { headers: headers, cancelToken: this.cancel_token_source.token }) .then((response) => {
				this.gambling_report_record = response.data;
				this.is_loading = false;

				return Promise.resolve(response);
			})
			.catch((error) => {
				if (global.axios.isCancel(error)) {
					console.log(error.message);
				} else {
					console.log(error.response);
					this.is_loading = false;

					return Promise.reject(error.response);
				}
			});
		},
		showBetslipsGamblingReport(payload) {
			// Check and cancel the previous request
			if (this.cancel_token_source) {
				this.cancel_token_source.cancel('Previous request canceled');
			}

			// Create a new cancel token
			this.cancel_token_source = global.axios.CancelToken.source();

			let headers = {
				'Accept': 'application/json'
			};

			this.is_loading = true;
			// this.gambling_report_record = {};
			this.selected_customer_id = payload.customer_id;
			this.selected_report = 'betslips';

			return global.axios.post(process.env.VUE_APP_API_URL + '/account/affiliate/report/betslips', payload, { headers: headers, cancelToken: this.cancel_token_source.token }) .then((response) => {
				this.gambling_report_record = response.data;
				this.is_loading = false;

				return Promise.resolve(response);
			})
			.catch((error) => {
				if (global.axios.isCancel(error)) {
					console.log(error.message);
				} else {
					console.log(error.response);
					this.is_loading = false;

					return Promise.reject(error.response);
				}
			});
		},
		resetPerPage() {
			this.per_page = 100;
		},
		fetchAgentInfo() {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/info', { headers: headers }).then((response) => {
				this.agent_info = response.data;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},

		fetchSportsbookGroupLimits() {
			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/limits') .then((response) => {
				this.sportsbook_group_limits = response.data.group_limits;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		fetchSportsbookGroupLimitsInfo() {
			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/limits/detailed') .then((response) => {
				this.sportsbook_group_limits_info = response.data.group_limits;

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		fetchedDetailedSportsbookGroupLimits(group_limit_id) {
			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/limits/info', {params: {group_limit_id: group_limit_id}}) .then((response) => {

				return Promise.resolve(response.data);
			}).catch((error) => {
				console.log(error.response);

				return Promise.reject(error.response);
			});
		},
		fetchAgentLimits() {
			let headers = {
				'Accept': 'application/json'
			};

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/affiliate/agent-limits', { headers: headers })
				.then((response) => {
					return Promise.resolve(response.data);
				}).catch((error) => {
					console.log(error.response);
					return Promise.reject(error.response);
				});
		},
		validateBettingLimits(customerLimits, agentLimits, translateFn = null) {
			const validationErrors = {};
			if (!agentLimits || !customerLimits) {
				return { isValid: true, errors: validationErrors };
			}

			const formatLimit = (val) => {
				if (val === null || val === undefined || val === '') return '';
				const num = Number(val);
				return isNaN(num) ? String(val) : num.toLocaleString();
			};

			const renderErrorMessage = (key, limitValue, fallbackTemplate) => {
				const formatted = formatLimit(limitValue);
				if (typeof translateFn === 'function') {
					const translated = translateFn(key, { value: formatted });
					if (translated && translated !== key) {
						return translated.replace(/:value/g, formatted).replace(/\{value\}/g, formatted);
					}
				}
				return fallbackTemplate.replace(/:value/g, formatted).replace(/\{value\}/g, formatted);
			};

			// Sportsbook
			if (customerLimits.sportsbook_min_stake != null && customerLimits.sportsbook_min_stake !== '' && agentLimits.sportsbook_min_stake != null) {
				if (Number(customerLimits.sportsbook_min_stake) < Number(agentLimits.sportsbook_min_stake)) {
					validationErrors.sportsbook_min_stake = renderErrorMessage(
						'agent.min_stake_too_low',
						agentLimits.sportsbook_min_stake,
						'Minimum stake cannot be lower than :value.'
					);
				}
			}
			if (customerLimits.sportsbook_max_stake != null && customerLimits.sportsbook_max_stake !== '' && agentLimits.sportsbook_max_stake != null) {
				if (Number(customerLimits.sportsbook_max_stake) > Number(agentLimits.sportsbook_max_stake)) {
					validationErrors.sportsbook_max_stake = renderErrorMessage(
						'agent.max_stake_too_high',
						agentLimits.sportsbook_max_stake,
						'Maximum stake cannot be higher than :value.'
					);
				}
			}

			if (customerLimits.sportsbook_min_stake != null && customerLimits.sportsbook_min_stake !== '' && customerLimits.sportsbook_max_stake != null && customerLimits.sportsbook_max_stake !== '') {
				if (Number(customerLimits.sportsbook_min_stake) > Number(customerLimits.sportsbook_max_stake)) {
					validationErrors.sportsbook_max_stake = renderErrorMessage(
						'agent.min_stake_exceeds_max_stake',
						customerLimits.sportsbook_max_stake,
						'Minimum stake cannot be higher than :value.'
					);
				}
			}
			if (customerLimits.sportsbook_max_turnover != null && customerLimits.sportsbook_max_turnover !== '' && agentLimits.sportsbook_max_turnover != null) {
				if (Number(customerLimits.sportsbook_max_turnover) > Number(agentLimits.sportsbook_max_turnover)) {
					validationErrors.sportsbook_max_turnover = renderErrorMessage(
						'agent.max_turnover_too_high',
						agentLimits.sportsbook_max_turnover,
						'Maximum turnover cannot be higher than :value.'
					);
				}
			}

			if (customerLimits.same_fixture_count != null && customerLimits.same_fixture_count !== '' && agentLimits.same_fixture_count != null) {
				if (Number(customerLimits.same_fixture_count) > Number(agentLimits.same_fixture_count)) {
					validationErrors.same_fixture_count = renderErrorMessage(
						'agent.same_fixture_count_too_high',
						agentLimits.same_fixture_count,
						'Same fixture count cannot be higher than :value.'
					);
				}
			}
			if (customerLimits.same_odd_count != null && customerLimits.same_odd_count !== '' && agentLimits.same_odd_count != null) {
				if (Number(customerLimits.same_odd_count) > Number(agentLimits.same_odd_count)) {
					validationErrors.same_odd_count = renderErrorMessage(
						'agent.same_odd_count_too_high',
						agentLimits.same_odd_count,
						'Same odd count cannot be higher than :value.'
					);
				}
			}
			if (customerLimits.minimum_price_bet != null && customerLimits.minimum_price_bet !== '' && agentLimits.minimum_price_bet != null) {
				if (Number(customerLimits.minimum_price_bet) < Number(agentLimits.minimum_price_bet)) {
					validationErrors.minimum_price_bet = renderErrorMessage(
						'agent.minimum_price_bet_too_low',
						agentLimits.minimum_price_bet,
						'Minimum price bet cannot be lower than :value.'
					);
				}
			}
			if (customerLimits.maximum_price_betslip != null && customerLimits.maximum_price_betslip !== '' && agentLimits.maximum_price_betslip != null) {
				if (Number(customerLimits.maximum_price_betslip) > Number(agentLimits.maximum_price_betslip)) {
					validationErrors.maximum_price_betslip = renderErrorMessage(
						'agent.maximum_price_betslip_too_high',
						agentLimits.maximum_price_betslip,
						'Maximum price betslip cannot be higher than :value.'
					);
				}
			}

			// Casino
			if (customerLimits.casino_min_stake != null && customerLimits.casino_min_stake !== '' && agentLimits.casino_min_stake != null) {
				if (Number(customerLimits.casino_min_stake) < Number(agentLimits.casino_min_stake)) {
					validationErrors.casino_min_stake = renderErrorMessage(
						'agent.min_stake_too_low',
						agentLimits.casino_min_stake,
						'Minimum stake cannot be lower than :value.'
					);
				}
			}
			if (customerLimits.casino_max_stake != null && customerLimits.casino_max_stake !== '' && agentLimits.casino_max_stake != null) {
				if (Number(customerLimits.casino_max_stake) > Number(agentLimits.casino_max_stake)) {
					validationErrors.casino_max_stake = renderErrorMessage(
						'agent.max_stake_too_high',
						agentLimits.casino_max_stake,
						'Maximum stake cannot be higher than :value.'
					);
				}
			}
			if (customerLimits.casino_min_stake != null && customerLimits.casino_min_stake !== '' && customerLimits.casino_max_stake != null && customerLimits.casino_max_stake !== '') {
				if (Number(customerLimits.casino_min_stake) > Number(customerLimits.casino_max_stake)) {
					validationErrors.casino_max_stake = renderErrorMessage(
						'agent.min_stake_exceeds_max_stake',
						customerLimits.casino_max_stake,
						'Minimum stake cannot be higher than :value.'
					);
				}
			}

			// Live Casino
			if (customerLimits.live_casino_min_stake != null && customerLimits.live_casino_min_stake !== '' && agentLimits.live_casino_min_stake != null) {
				if (Number(customerLimits.live_casino_min_stake) < Number(agentLimits.live_casino_min_stake)) {
					validationErrors.live_casino_min_stake = renderErrorMessage(
						'agent.min_stake_too_low',
						agentLimits.live_casino_min_stake,
						'Minimum stake cannot be lower than :value.'
					);
				}
			}
			if (customerLimits.live_casino_max_stake != null && customerLimits.live_casino_max_stake !== '' && agentLimits.live_casino_max_stake != null) {
				if (Number(customerLimits.live_casino_max_stake) > Number(agentLimits.live_casino_max_stake)) {
					validationErrors.live_casino_max_stake = renderErrorMessage(
						'agent.max_stake_too_high',
						agentLimits.live_casino_max_stake,
						'Maximum stake cannot be higher than :value.'
					);
				}
			}
			if (customerLimits.live_casino_min_stake != null && customerLimits.live_casino_min_stake !== '' && customerLimits.live_casino_max_stake != null && customerLimits.live_casino_max_stake !== '') {
				if (Number(customerLimits.live_casino_min_stake) > Number(customerLimits.live_casino_max_stake)) {
					validationErrors.live_casino_max_stake = renderErrorMessage(
						'agent.min_stake_exceeds_max_stake',
						customerLimits.live_casino_max_stake,
						'Minimum stake cannot be higher than :value.'
					);
				}
			}

			return {
				isValid: Object.keys(validationErrors).length === 0,
				errors: validationErrors
			};
		},
		fetchCustomerLimits(customer_id) {
			return global.axios.get(process.env.VUE_APP_API_URL + "/account/affiliate/users/" + customer_id + "/limits").then((response) => {
				return Promise.resolve(response.data);
			})
			.catch((error) => {
				console.log(error.response);
				return Promise.reject(error.response);
			});
		}
	},
});
